import { Stack, useRouter } from 'expo-router';
import { Home } from 'lucide-react-native';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';

/**
 * Custom 404 page. With web.output = "single" the EAS Hosting worker
 * always serves index.html and client-side routing decides; this route
 * still catches unknown paths once the app boots. Keeps the warm
 * Homestead vibe instead of dumping the user on the dark Workers
 * fallback ("The worker has no matching route handler for this path").
 */
export default function NotFound() {
  const router = useRouter();
  return (
    <>
      <Stack.Screen options={{ title: 'Not found' }} />
      <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
        <View style={styles.inner}>
          <View style={styles.iconWrap}>
            <Home size={28} color={Palette.forest} strokeWidth={1.8} />
          </View>
          <Text style={styles.h1}>This page wandered off</Text>
          <Text style={styles.sub}>
            We couldn&apos;t find what you were looking for. The link might be
            stale, or we may have moved things around.
          </Text>
          {/* Plain Pressable instead of Link asChild — asChild dropped the
              function-form style on device, leaving cream-on-cream text. */}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Take me home"
            onPress={() => router.replace('/')}
            style={({ pressed }) => [
              styles.cta,
              pressed && { transform: [{ scale: 0.98 }], opacity: 0.92 },
            ]}>
            <Home size={16} color={Palette.cream} strokeWidth={2.2} />
            <Text style={styles.ctaText}>Take me home</Text>
          </Pressable>
        </View>
      </SafeAreaView>
    </>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  inner: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.five,
    gap: 14,
  },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: 18,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  h1: {
    fontFamily: Fonts.serifBold,
    fontSize: 22,
    color: Palette.ink,
    letterSpacing: -0.3,
    textAlign: 'center',
  },
  sub: {
    fontFamily: Fonts.sansMedium,
    fontSize: 14,
    lineHeight: 20,
    color: Palette.inkSoft,
    textAlign: 'center',
    maxWidth: 360,
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: Spacing.three,
    backgroundColor: Palette.forest,
    paddingHorizontal: 18,
    paddingVertical: 12,
    borderRadius: Radius.lg,
  },
  ctaText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.cream,
  },
});
